'use client';
import React, { useState } from 'react';
import EditableText from './EditableText';
import EditableImage from './EditableImage';
import { useEditMode } from './EditModeProvider';
import ColorPicker from './ColorPicker';
import LeadModal from './ui/LeadModal';

const BusinessGoals = () => {
    const { getContent } = useEditMode();
    const [isModalOpen, setIsModalOpen] = useState(false);

    const colorSettings = [
        { key: 'goals_bg_color_light', label: 'Фон секции (Светлая тема)', default: '#f3f4f6' },
        { key: 'goals_bg_color_dark', label: 'Фон секции (Темная тема)', default: '#09090b' },
        { key: 'btn_free_bg_color', label: 'Фон кнопки', default: '#17c6a6' },
        { key: 'btn_free_text_color', label: 'Текст кнопки', default: '#FFFFFF' },
    ];

    const sectionStyle = {
        '--goals-bg-light': getContent('goals_bg_color_light', '#f3f4f6'),
        '--goals-bg-dark': getContent('goals_bg_color_dark', '#09090b'),
        '--btn-free-bg': getContent('btn_free_bg_color', '#17c6a6'),
        '--btn-free-text': getContent('btn_free_text_color', '#FFFFFF'),
    };

    return (
        <section style={sectionStyle} className="bg-[var(--goals-bg-light)] dark:bg-[var(--goals-bg-dark)] pt-10 pb-16 w-full relative">
            <ColorPicker settings={colorSettings} />
            <div className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
                {/* Title */}
                <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-center text-gray-900 dark:text-white mb-12 max-w-4xl mx-auto leading-tight">
                    <EditableText contentKey="goals_title" defaultValue="Санэкс поможет контролировать оборудование и процессы 24/7" />
                </h2>

                {/* Grid */}
                <div className="flex flex-col gap-6">
                    {/* Row 1 */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Card 1 */}
                        <div className="bg-[#ffffff] dark:bg-zinc-900/50 rounded-[2rem] pt-8 px-8 sm:pt-10 sm:px-10 pb-0 flex flex-col overflow-hidden transition-transform duration-300 hover:scale-[1.02]">
                            <h3 className="text-2xl sm:text-3xl font-semibold text-gray-900 dark:text-white mb-4">
                                <EditableText contentKey="goal_1_title" defaultValue="Контролируйте оборудование 24/7" />
                            </h3>
                            <p className="text-gray-600 dark:text-gray-400 mb-8 text-sm sm:text-base leading-relaxed">
                                <EditableText contentKey="goal_1_desc" defaultValue="Автоматический мониторинг температурного режима в холодильниках и помещениях. Получайте мгновенные уведомления о любых отклонениях." />
                            </p>
                            <div className="mt-auto w-full h-48 sm:h-56 relative overflow-hidden flex items-end justify-center">
                                <EditableImage contentKey="goal_1_img" defaultSrc="/images/goal1.png" className="w-full h-full object-contain object-bottom" />
                            </div>
                        </div>
                        {/* Card 2 */}
                        <div className="bg-[#ffffff] dark:bg-zinc-900/50 rounded-[2rem] pt-8 px-8 sm:pt-10 sm:px-10 pb-0 flex flex-col overflow-hidden transition-transform duration-300 hover:scale-[1.02]">
                            <h3 className="text-2xl sm:text-3xl font-semibold text-gray-900 dark:text-white mb-4">
                                <EditableText contentKey="goal_2_title" defaultValue="Забудьте о ручном ведении журналов" />
                            </h3>
                            <p className="text-gray-600 dark:text-gray-400 mb-8 text-sm sm:text-base leading-relaxed">
                                <EditableText contentKey="goal_2_desc" defaultValue="Система автоматически заполняет журналы учета температур и влажности. Больше никакой бумажной рутины и ошибок человеческого фактора." />
                            </p>
                            <div className="mt-auto w-full h-48 sm:h-56 relative overflow-hidden flex items-end justify-center">
                                <EditableImage contentKey="goal_2_img" defaultSrc="/images/goal2.png" className="w-full h-full object-contain object-bottom" />
                            </div>
                        </div>
                    </div>

                    {/* Row 2 */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {/* Card 3 */}
                        <div className="bg-[#ffffff] dark:bg-zinc-900/50 rounded-[2rem] pt-8 px-8 sm:pt-10 sm:px-8 pb-0 flex flex-col overflow-hidden transition-transform duration-300 hover:scale-[1.02]">
                            <h3 className="text-xl sm:text-2xl font-semibold text-gray-900 dark:text-white mb-4">
                                <EditableText contentKey="goal_3_title" defaultValue="Предотвращайте порчу продукции" />
                            </h3>
                            <p className="text-gray-600 dark:text-gray-400 mb-8 text-sm leading-relaxed">
                                <EditableText contentKey="goal_3_desc" defaultValue="Своевременные оповещения о сбоях оборудования позволяют спасти дорогостоящие лекарства, продукты или сырье." />
                            </p>
                            <div className="mt-auto w-full h-48 sm:h-56 relative overflow-hidden flex items-end justify-center">
                                <EditableImage contentKey="goal_3_img" defaultSrc="/images/goal3.png" className="w-full h-full object-contain object-bottom" />
                            </div>
                        </div>
                        {/* Card 4 */}
                        <div className="bg-[#ffffff] dark:bg-zinc-900/50 rounded-[2rem] pt-8 px-8 sm:pt-10 sm:px-8 pb-0 flex flex-col overflow-hidden transition-transform duration-300 hover:scale-[1.02]">
                            <h3 className="text-xl sm:text-2xl font-semibold text-gray-900 dark:text-white mb-4">
                                <EditableText contentKey="goal_4_title" defaultValue="Анализируйте отчеты в один клик" />
                            </h3>
                            <p className="text-gray-600 dark:text-gray-400 mb-8 text-sm leading-relaxed">
                                <EditableText contentKey="goal_4_desc" defaultValue="Выгружайте готовые отчёты для контролирующих органов (Роспотребнадзор) за любой период в форматах PDF и Excel." />
                            </p>
                            <div className="mt-auto w-full h-48 sm:h-56 relative overflow-hidden flex items-end justify-center">
                                <EditableImage contentKey="goal_4_img" defaultSrc="/images/goal4.png" className="w-full h-full object-contain object-bottom" />
                            </div>
                        </div>
                        {/* Card 5 */}
                        <div className="bg-[#ffffff] dark:bg-zinc-900/50 rounded-[2rem] pt-8 px-8 sm:pt-10 sm:px-8 pb-0 flex flex-col overflow-hidden transition-transform duration-300 hover:scale-[1.02]">
                            <h3 className="text-xl sm:text-2xl font-semibold text-gray-900 dark:text-white mb-4">
                                <EditableText contentKey="goal_5_title" defaultValue="Управляйте бизнесом из любой точки" />
                            </h3>
                            <p className="text-gray-600 dark:text-gray-400 mb-8 text-sm leading-relaxed">
                                <EditableText contentKey="goal_5_desc" defaultValue="Следите за показателями всех ваших филиалов и оборудования через удобное мобильное приложение или веб-панель." />
                            </p>
                            <div className="mt-auto w-full h-48 sm:h-56 relative overflow-hidden flex items-end justify-center">
                                <EditableImage contentKey="goal_5_img" defaultSrc="/images/goal5.png" className="w-full h-full object-contain object-bottom" />
                            </div>
                        </div>
                    </div>
                </div>

                {/* CTA Button */}
                <div className="mt-12 flex justify-center">
                    <button onClick={() => setIsModalOpen(true)} className="bg-[var(--btn-free-bg)] text-[var(--btn-free-text)] hover:opacity-90 font-semibold py-4 px-8 rounded-2xl transition-colors duration-300 shadow-sm">
                        <EditableText contentKey="goals_cta" defaultValue="Попробовать бесплатно" />
                    </button>
                </div>
            </div>
            <LeadModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} buttonColor={getContent('btn_free_bg_color', '#17c6a6')} />
        </section>
    );
};

export default BusinessGoals;